import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfSize,
  PdfRect,
  ETextAlign,
  PdfFont,
  PdfBrush,
  PdfPen,
  PdfColor,
  PdfPoint,
  PdfUnit,
  PageGen,
} from '../../pdf/pdf';
import { parseXAML } from './parser';
import type { UIControl, Annotation } from './parser';
import { measureControl, drawControl } from './controls/registry';
import { ANNOTATION_COLORS } from './controls/primitives';
import './controls/basic';
import './controls/containers';
import './controls/complex';

const DEFAULT_NOTES_WIDTH = 50;
const ARROW_GAP = 5;

function wrapText(text: string, font: PdfFont, maxWidth: number, page: PageGen): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && page.measureString(candidate, font).width > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
}

export const uiModule: IModule = {
  name: 'ui',

  parse(source: string, attrs: Record<string, string>) {
    const { controls, annotations } = parseXAML(source);
    return {
      type: 'ui',
      attrs,
      content: source,
      controls,
      annotations,
      notesPanelWidth: parseFloat(attrs.notesWidth) || PdfUnit.FromMillimeters(DEFAULT_NOTES_WIDTH),
    } as ModuleContent & {
      controls: UIControl[];
      annotations: Annotation[];
      notesPanelWidth: number;
    };
  },

  measure(
    content: ModuleContent & {
      controls: UIControl[];
      annotations: Annotation[];
      notesPanelWidth: number;
    },
    params: ModuleParams
  ): PdfSize {
    const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
    const page = params.page!;

    let notesWidth = 0;
    let notesHeight = 0;
    if (content.annotations.length > 0) {
      notesWidth = content.notesPanelWidth;
      const noteFont = new PdfFont(PdfFont.FONT_NAME, Math.max(8, font.size - 2));
      for (const ann of content.annotations) {
        const textSize = page?.measureString(ann.text, noteFont) || new PdfSize(30, 5);
        notesHeight += textSize.height + 3;
      }
    }

    const annotationSpace =
      content.annotations.length > 0 ? PdfUnit.FromMillimeters(ARROW_GAP) + notesWidth : 0;

    let maxWidth = 0;
    let maxHeight = 0;

    for (const control of content.controls) {
      const size = measureControl(
        control,
        { width: Number.MAX_VALUE, height: Number.MAX_VALUE },
        { font, page }
      );
      maxWidth = Math.max(maxWidth, size.width);
      maxHeight = Math.max(maxHeight, size.height);
    }

    return new PdfSize(maxWidth + annotationSpace, Math.max(maxHeight, notesHeight));
  },

  draw(
    content: ModuleContent & {
      controls: UIControl[];
      annotations: Annotation[];
      notesPanelWidth: number;
    },
    rect: PdfRect,
    _align: ETextAlign,
    params: ModuleParams
  ): void {
    const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
    const page = params.page!;

    const availableWidth = rect.width;
    let currentY = rect.y;

    const annotationHandler = (ann: Annotation, r: PdfRect) => {
      ann.controlRect = r;
    };

    for (const control of content.controls) {
      const size = measureControl(
        control,
        { width: availableWidth, height: Number.MAX_VALUE },
        { font, page }
      );

      const controlRect = new PdfRect(rect.x, currentY, availableWidth, size.height);
      drawControl(control, controlRect, { font, page, annotationHandler });
      currentY += size.height + PdfUnit.FromMillimeters(2);
    }

    if (content.annotations.length > 0) {
      const notesStartX = rect.x + rect.width + PdfUnit.FromMillimeters(ARROW_GAP);
      const notesTextWidth = content.notesPanelWidth - PdfUnit.FromMillimeters(4);
      let notesY = rect.y;

      const noteFont = new PdfFont(PdfFont.FONT_NAME, Math.max(8, font.size - 2));
      const lineH = page.measureString('Ag', noteFont).height;

      for (const ann of content.annotations) {
        if (!ann.controlRect) continue;

        const annColor = ANNOTATION_COLORS[ann.type];
        const pen = new PdfPen(annColor, 0.5);

        const startPoint = new PdfPoint(
          ann.controlRect.x + ann.controlRect.width,
          ann.controlRect.y + ann.controlRect.height / 2
        );
        const endPoint = new PdfPoint(notesStartX, notesY + lineH / 2);

        const dx = endPoint.x - startPoint.x;
        const curvature = Math.min(
          Math.max(Math.abs(dx) * 0.35, PdfUnit.FromMillimeters(3)),
          PdfUnit.FromMillimeters(15)
        );
        const cp1 = new PdfPoint(startPoint.x + curvature, startPoint.y);
        const cp2 = new PdfPoint(endPoint.x - curvature, endPoint.y);

        page.drawBeziers(pen, null, [startPoint, cp1, cp2, endPoint]);

        const arrowSize = PdfUnit.FromMillimeters(2);
        const angle = Math.atan2(endPoint.y - cp2.y, endPoint.x - cp2.x);
        page.drawPolygon(pen, new PdfBrush(annColor), [
          new PdfPoint(endPoint.x, endPoint.y),
          new PdfPoint(
            endPoint.x - arrowSize * Math.cos(angle - Math.PI / 6),
            endPoint.y - arrowSize * Math.sin(angle - Math.PI / 6)
          ),
          new PdfPoint(
            endPoint.x - arrowSize * Math.cos(angle + Math.PI / 6),
            endPoint.y - arrowSize * Math.sin(angle + Math.PI / 6)
          ),
        ]);

        const lines = wrapText(ann.text, noteFont, notesTextWidth, page);
        const textX = notesStartX + PdfUnit.FromMillimeters(2);
        for (const line of lines) {
          page.drawString(
            line,
            noteFont,
            new PdfBrush(annColor),
            null,
            new PdfRect(textX, notesY, notesTextWidth, lineH),
            ETextAlign.CenterLeft
          );
          notesY += lineH;
        }
        notesY += PdfUnit.FromMillimeters(2);
      }
    }
  },
};
